import RangeSliderTwoHandles from "@Components/RangeSlider/RangeSliderTwoHandles";
import { FilterComponentProps } from "@Pages/Search/components/FilterSection/components/ActiveFilterComponent/types";
import usePriceHistogram from "@Pages/Search/components/FilterSection/components/Filters/Budget/usePriceHistogram";
import { AppState } from "@Store/index";
import { shallowEqual, useSelector } from "react-redux";
import styles from "./budget.module.scss";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import wrapperHOC from "@Utils/wrapperHOC";
import { useMemo } from "react";
import currencyData from "constants/currencyConstants";
import {
  selectFilterCountPayload,
  selectPriceHistogram,
} from "@Pages/Search/data/searchPageReducer";

const Budget = ({ value, dispatch, onApplyClick }: FilterComponentProps<string | string[]>) => {
  const { region, currencyExchangeRates, priceHistogram, priceStats } = useSelector(
    (state: AppState) => ({
      region: state.searchPageData?.region,
      currencyExchangeRates: state.appData.currencyExchangeRates,
      // Aggregations sit one level deeper than the response envelope; the path lives in one place
      // so a change to any envelope is not a silent undefined here.
      priceHistogram: selectPriceHistogram(state),
      priceStats: selectFilterCountPayload(state),
    }),
    shallowEqual,
  );

  const {
    max,
    gridMin,
    interval,
    sliderMax,
    toSliderValue,
    catchAll,
    currency,
    selMin,
    selMax,
    onSliderChange,
    onSliderDragEnd,
  } = usePriceHistogram({
    value,
    priceHistogram,
    priceStats,
    region,
    dispatch,
    onApplyClick,
    // Keeps the pre-histogram label so existing dashboards split on it keep working.
    analyticsSection: "Inline Filters",
  });

  const handleOnDragEnd = (values?: [number, number]) => {
    gtagEventEmitter({
      action: "SP_filter_subsection_filter_value_selected",
      category: "SP_filter_subsection",
      value: "Budget",
    });
    onSliderDragEnd(values);
  };

  // The top of the range covers everything above it, so it reads as "£660+".
  const openEndedSuffix = catchAll && selMax >= max ? "+" : "";

  const budgetFilterText = useMemo(() => {
    const inRegionCurrency = `${currency}${selMin} - ${currency}${selMax}${openEndedSuffix}`;
    try {
      const baseCurrency = currencyExchangeRates?.baseCurrency;
      if (!baseCurrency) return inRegionCurrency;

      const currentCurrency = currencyData.find(
        (c) => c.symbol === currency || c.standard_name === currency || c.short_name === currency,
      );

      const userCurrency = currencyData.find((c) => c.standard_name === baseCurrency);

      const exchangeRate = currencyExchangeRates.rates?.find((item: any) => {
        const target = item.target_currency.toLowerCase();
        return (
          target === currentCurrency?.standard_name?.toLowerCase() ||
          target === currentCurrency?.short_name?.toLowerCase()
        );
      });

      if (!exchangeRate?.rate) return inRegionCurrency;

      const convertedValue1 = Math.ceil(selMin / exchangeRate.rate);
      const convertedValue2 = Math.ceil(selMax / exchangeRate.rate);

      return `${userCurrency?.symbol}${convertedValue1} - ${userCurrency?.symbol}${convertedValue2}${openEndedSuffix}`;
    } catch (error) {
      return inRegionCurrency;
    }
  }, [currencyExchangeRates, currencyData, currency, selMin, selMax, openEndedSuffix]);

  // The histogram bars are switched off in this inline panel for now; the slider still takes
  // its bounds and interval from the price_histogram aggregation.
  return (
    <div className={styles.rangeContainer}>
      <RangeSliderTwoHandles
        min={0}
        max={sliderMax}
        step={interval}
        valueOne={toSliderValue(selMin)}
        valueTwo={toSliderValue(selMax)}
        handleOnChange={onSliderChange}
        handleOnDragEnd={handleOnDragEnd}
        actualMinVal={gridMin}
      />
      <div className={styles.priceText}>{budgetFilterText}</div>
    </div>
  );
};

export default wrapperHOC(Budget, {
  componentName: "Budget-FilterSubsection-SearchListDesktop-SearchPage",
  showForChina: true,
});
